import React, { useState, useEffect } from 'react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { storage, db, auth } from '../firebase';
import { useUser } from '../UserContext';

function TestResultUpload() {
    const { user } = useUser();
    const [file, setFile] = useState(null);
    const [metadata, setMetadata] = useState({
        email: '',
        address: '',
        testDate: '',
        notes: ''
    });
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

    // Check if Firebase is initialized
    useEffect(() => {
        if (!storage || !db) {
            console.error('Firebase not initialized');
            setError('System initialization error');
        }
    }, []);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            // Check file size
            if (selectedFile.size > MAX_FILE_SIZE) {
                setError('File size exceeds 10MB limit');
                return;
            }

            // Check file type
            const validTypes = [
                'application/pdf',
                'image/jpeg',
                'image/png',
                'application/vnd.ms-excel',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'text/csv',
                'application/csv'
            ];

            console.log('Selected file type:', selectedFile.type);
            
            if (!validTypes.includes(selectedFile.type) && 
                !selectedFile.name.toLowerCase().endsWith('.csv')) { // Fallback check for CSV files
                setError('Invalid file type. Please upload a PDF, image, CSV, or Excel spreadsheet.');
                return;
            }
            setFile(selectedFile);
            setError('');
        }
    };

    const handleMetadataChange = (e) => {
        const { name, value } = e.target;
        setMetadata(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!file) {
            setError('Please select a file to upload');
            return;
        }

        setUploading(true);
        setError('');
        setSuccess('');

        try {
            // Check if Firebase is initialized
            if (!storage || !db) {
                throw new Error('Firebase not initialized');
            }

            // Log auth state
            console.log('Auth state:', auth.currentUser ? 'Authenticated' : 'Not authenticated');
            console.log('User context:', user ? 'Present' : 'Not present');

            // 1. First upload the file to Storage
            console.log('Starting file upload to Storage...');
            const timestamp = Date.now();
            const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
            const storageFilePath = `test-results/${timestamp}-${sanitizedFileName}`;
            console.log('Storage path:', storageFilePath);
            
            const fileRef = ref(storage, storageFilePath);
            const uploadResult = await uploadBytes(fileRef, file);
            console.log('Upload successful:', uploadResult);

            // 2. Get the download URL
            console.log('Getting download URL...');
            const downloadURL = await getDownloadURL(fileRef);
            console.log('Download URL obtained');

            // 3. Create Firestore document with the URL
            console.log('Creating Firestore document...');
            const docRef = await addDoc(collection(db, "testResults"), {
                userId: user?.uid || 'anonymous',
                email: metadata.email,
                address: metadata.address,
                testDate: metadata.testDate,
                notes: metadata.notes,
                fileName: sanitizedFileName,
                fileType: file.type,
                uploadDate: Timestamp.now(),
                fileUrl: downloadURL,
                status: 'pending'
            });
            console.log('Firestore document created:', docRef.id);

            setSuccess('Test results uploaded successfully!');
            setFile(null);
            setMetadata({
                email: '',
                address: '',
                testDate: '',
                notes: ''
            });
            
            // Reset file input
            const fileInput = document.getElementById('file-input');
            if (fileInput) fileInput.value = '';
        } catch (err) {
            console.error('Error uploading test results:', err);
            let errorMessage = 'Failed to upload test results: ';
            if (err.code === 'storage/unauthorized') {
                errorMessage += 'Permission denied. Please try again.';
            } else if (err.code === 'storage/unknown') {
                errorMessage += 'Unknown error occurred. Please try again.';
            } else {
                errorMessage += err.message;
            }
            setError(errorMessage);
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-4">Upload Test Results</h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Upload File (PDF, Image, CSV, or Excel)
                    </label>
                    <input
                        id="file-input"
                        type="file"
                        onChange={handleFileChange}
                        accept=".pdf,.jpg,.jpeg,.png,.xls,.xlsx,.csv"
                        className="w-full p-2 border rounded"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address
                    </label>
                    <input
                        type="email"
                        name="email"
                        value={metadata.email}
                        onChange={handleMetadataChange}
                        required
                        className="w-full p-2 border rounded"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Location Address
                    </label>
                    <input
                        type="text"
                        name="address"
                        value={metadata.address}
                        onChange={handleMetadataChange}
                        required
                        className="w-full p-2 border rounded"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Test Date
                    </label>
                    <input
                        type="date"
                        name="testDate"
                        value={metadata.testDate}
                        onChange={handleMetadataChange}
                        required
                        className="w-full p-2 border rounded"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Additional Notes
                    </label>
                    <textarea
                        name="notes"
                        value={metadata.notes}
                        onChange={handleMetadataChange}
                        className="w-full p-2 border rounded"
                        rows="3"
                    />
                </div>

                {error && (
                    <div className="text-red-500 text-sm">{error}</div>
                )}

                {success && (
                    <div className="text-green-500 text-sm">{success}</div>
                )}

                <button
                    type="submit"
                    disabled={uploading}
                    className={`w-full bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600 transition-colors
                        ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                    {uploading ? 'Uploading...' : 'Upload Test Results'}
                </button>
            </form>
        </div>
    );
}

export default TestResultUpload;
